import { logger } from '@inboundx/shared/platform';
import type { Database, Json } from '@inboundx/shared/types';

import type { KnowledgeOperationStatus } from '@/domain/knowledgeOperations';
import {
    clearPendingRequestKey,
    pendingRequestKey,
    shouldRetainRequestKey,
} from '@/lib/requestIdempotency';
import { supabase } from '@/lib/supabase';

import {
    buildApiUrl,
    getAdminApiBaseUrl,
    getSupabaseAccessToken,
    parseErrorDetail,
    type ServiceErrorFactory,
} from './adminApi';

type CrawlRunRow = Database['knowledge']['Tables']['crawl_runs']['Row'];
type CorpusDocumentRow = Database['knowledge']['Views']['corpus_documents']['Row'];

const SUPABASE_PAGE_SIZE = 1000;

export type PageDecision = 'include' | 'exclude';
export type RunStatus = 'queued' | 'mapping' | 'mapped' | 'failed';

export const ACTIVE_WEBSITE_SYNC_RUN_STATUSES = new Set<RunStatus>(['queued', 'mapping']);

export interface WebsiteSyncRun {
    id: string;
    workspaceId: string;
    environment: string;
    targetUrl: string;
    status: RunStatus;
    mapOptions: Json;
    metadata: Json;
    rawMapUri: string | null;
    mappedCount: number;
    includedCount: number;
    excludedCount: number;
    errorMessage: string | null;
    createdAt: string;
    startedAt: string | null;
    completedAt: string | null;
    failedAt: string | null;
}

export interface WebsiteSyncPage {
    id: string;
    runId: string | null;
    pageId: string;
    canonicalUrl: string;
    normalizedPath: string;
    title: string | null;
    description: string | null;
    decision: PageDecision;
    decisionReason: string | null;
    documentStatus: string;
    currentSnapshotId: string | null;
    snapshotVersion: number | null;
    processedMarkdownRef: string | null;
    lastSeenAt: string;
}

export interface WebsiteSyncPagesFilters {
    decision?: PageDecision | 'all';
    search?: string;
    page?: number;
    pageSize?: number;
}

export interface WebsiteSyncPagesResult {
    items: WebsiteSyncPage[];
    total: number;
}

/**
 * Included website documents discovered after a given instant — i.e. pages the
 * auto-mapper selected that have not yet been pushed to the knowledge base.
 * `truncated` is set when more than `NEWLY_SELECTED_LIMIT` matched, so the
 * caller can fall back to a full refresh instead of a bounded policy apply.
 */
export interface NewlySelectedDocuments {
    documentIds: string[];
    total: number;
    truncated: boolean;
}

export interface WebsiteSyncCapabilities {
    selected_refresh_enabled: boolean;
}

export interface UnfinishedWebsiteDocuments {
    document_ids: string[];
    total: number;
}

export interface RefreshWebsiteDocumentsArgs {
    workspaceId: string;
    documentIds: string[];
}

export interface RefreshWebsiteDocumentsResponse {
    operation_id: string;
    status: KnowledgeOperationStatus;
    status_url: string;
    deduplicated: boolean;
    progress_owner: 'knowledge_operation';
}

export interface StartWebsiteRescanArgs {
    workspaceId: string;
    /** Which crawl root to re-scan. Optional while a client has a single root. */
    websiteSourceId?: string;
}

/** One crawl root of a client. A client may sync several. */
export interface WebsiteSource {
    id: string;
    site_domain: string;
    host: string;
    root_url: string;
    generation: number;
}

interface MappingRunResponseBase {
    id: string;
    workspaceId: string;
    environment: string;
    target_url: string;
}

export interface StartWebsiteRescanResponse extends MappingRunResponseBase {
    status: KnowledgeOperationStatus;
    operation_id: string;
    status_url: string;
    progress_owner: 'knowledge_operation';
}

export type WebsiteRescanReviewStatus = 'scanning' | 'ready' | 'approved' | 'dismissed' | 'stale';

export type WebsiteRescanReviewChangeKind = 'added' | 'missing' | 'returning' | 'unchanged';

export interface WebsiteRescanReviewSummary {
    id: string;
    site_domain: string;
    environment: string;
    status: WebsiteRescanReviewStatus;
    scan_operation_id: string;
    scan_operation_status: KnowledgeOperationStatus;
    crawl_run_id: string;
    website_source_id: string;
    apply_operation_id: string | null;
    apply_operation_status: KnowledgeOperationStatus | null;
    added_count: number;
    missing_count: number;
    returning_count: number;
    unchanged_count: number;
    /** Included pages found again that a refresh would fetch. */
    refresh_count: number;
    ready_at: string | null;
    approved_at: string | null;
    dismissed_at: string | null;
    created_at: string;
    updated_at: string;
}

export interface WebsiteRescanReviewPage {
    id: string;
    review_id: string;
    change_kind: WebsiteRescanReviewChangeKind;
    canonical_url: string;
    path: string;
    title: string | null;
    description: string | null;
    document_id: string | null;
    scrape_policy: string | null;
    document_status: string | null;
    is_deleted: boolean | null;
    current_snapshot_id: string | null;
    proposed_decision?: 'include' | 'exclude' | null;
    proposed_reason?: string | null;
    was_in_knowledge: boolean;
    last_seen_at: string | null;
}

export interface CurrentWebsiteRescanReviewResponse {
    review: WebsiteRescanReviewSummary | null;
}

export interface WebsiteRescanReviewPagesResponse {
    items: WebsiteRescanReviewPage[];
    next_cursor: string | null;
}

export interface WebsiteRescanReviewPagesResult {
    items: WebsiteRescanReviewPage[];
}

export interface ApplyWebsiteRescanReviewArgs {
    reviewId: string;
    workspaceId: string;
    selectedPageIds: string[];
    keptMissingPageIds: string[];
    /** Also fetch again the included pages this scan found. */
    refreshExistingPages: boolean;
}

export interface ApplyWebsiteRescanReviewResponse {
    review: WebsiteRescanReviewSummary;
    operation_id: string | null;
    status: KnowledgeOperationStatus | null;
    status_url: string | null;
    deduplicated: boolean;
    progress_owner: 'knowledge_operation' | null;
    updated_count: number;
}

export interface DismissWebsiteRescanReviewArgs {
    reviewId: string;
    workspaceId: string;
}

export interface UpdatePageDecisionArgs {
    workspaceId: string;
    pageId: string;
    decision: PageDecision;
    reason?: string | null;
    persistOverride?: boolean;
}

export interface BulkUpdatePageDecisionArgs {
    workspaceId: string;
    runId: string;
    pageIds: string[];
    decision: PageDecision;
    reason?: string | null;
    persistOverride?: boolean;
}

export interface BulkUpdateDocumentDecisionArgs {
    workspaceId: string;
    documentIds: string[];
    decision: PageDecision;
    reason?: string | null;
    persistOverride?: boolean;
}

export interface ApplyWebsiteDocumentPolicyChangesArgs {
    workspaceId: string;
    includeDocumentIds: string[];
    excludeDocumentIds: string[];
    reason?: string | null;
}

interface DecisionUpdateResponseBase {
    updated_count: number;
}

export type DecisionUpdateResponse = DecisionUpdateResponseBase &
    (
        | {
              operation_id: string;
              status: KnowledgeOperationStatus;
              status_url: string;
              progress_owner: 'knowledge_operation';
          }
        | {
              operation_id: null;
              status: null;
              status_url: null;
              progress_owner: 'legacy';
          }
    );

export class WebsiteSyncError extends Error {
    status: number | null;

    constructor(message: string, status: number | null = null) {
        super(message);
        this.name = 'WebsiteSyncError';
        this.status = status;
    }
}

const fail: ServiceErrorFactory = (message, status = null) => new WebsiteSyncError(message, status);

const defaultCrawlErrorMessage = "Rose couldn't finish the last website check. Please try again.";
const crawlErrorMessages = new Map<string, string>(
    Object.entries({
        crawl_failed: defaultCrawlErrorMessage,
        crawl_cancelled: 'The website check was cancelled. You can start another check.',
        map_coverage_incomplete: 'Rose could not check the whole website. Please try again.',
        content_stage_timeout: 'The website check timed out. Please try again.',
        content_stage_transport_error:
            'A connection interrupted the website check. Please try again.',
        content_stage_http_error:
            'A service could not complete the website check. Please try again.',
        content_stage_unexpected_error: defaultCrawlErrorMessage,
        page_scrape_failed: 'Some website content could not be fetched. Please try again.',
        page_clean_failed: 'Some website content could not be processed. Please try again.',
        website_redirects_elsewhere:
            'This website redirects to another domain, so Rose cannot scan it. Add the domain it redirects to as a website, then retire this one (contact support@userose.ai).',
    })
);

function mapRun(row: CrawlRunRow): WebsiteSyncRun {
    const excludedCount = Math.max(row.urls_discovered - row.urls_included, 0);
    return {
        id: row.id,
        workspaceId: row.workspace_id ?? '',
        environment: row.environment,
        targetUrl: row.target_url,
        status: row.status as RunStatus,
        mapOptions: row.options,
        metadata: row.metadata,
        rawMapUri: row.raw_map_uri,
        mappedCount: row.urls_discovered,
        includedCount: row.urls_included,
        excludedCount,
        errorMessage: row.error
            ? (crawlErrorMessages.get(row.error) ?? defaultCrawlErrorMessage)
            : null,
        createdAt: row.created_at,
        startedAt: row.started_at,
        completedAt: row.status === 'mapped' ? row.finished_at : null,
        failedAt: row.status === 'failed' ? row.finished_at : null,
    };
}

function mapPage(row: CorpusDocumentRow): WebsiteSyncPage {
    return {
        id: row.document_id ?? '',
        runId: row.latest_run_id,
        pageId: row.document_id ?? '',
        canonicalUrl: row.canonical_url ?? row.source_url ?? row.path ?? '',
        normalizedPath: row.path ?? '',
        title: row.title,
        description: row.description,
        decision: row.decision === 'include' ? 'include' : 'exclude',
        decisionReason: row.decision_reason,
        documentStatus: row.document_status ?? '',
        currentSnapshotId: row.current_snapshot_id,
        snapshotVersion: row.snapshot_version,
        processedMarkdownRef: row.processed_markdown_ref,
        lastSeenAt: row.last_seen_at ?? row.created_at ?? new Date(0).toISOString(),
    };
}

function escapeIlikeSearch(input: string): string {
    return input.replace(/[\\%_]/g, '\\$&');
}

/**
 * Has this workspace's website content been scraped and snapshotted at least once?
 *
 * Asked of the corpus rather than of `knowledge.ingestion_operations`, because
 * the operation log is filtered to the API's own `IX_ENVIRONMENT` while the
 * knowledge base it records is populated per environment by separate runs. A
 * site onboarded through another deployment can have a real knowledge base in
 * this one and no operation row to prove it (IX-4822).
 *
 * Known ceiling: `last_processed_at` is NOT proof of LightRAG ingestion. The
 * scraper stamps it at snapshot time (`ixscraping/stores/supabase_store.py`),
 * before the document-loader runs, so this returns true during the
 * scraped-but-not-yet-loaded window and stays true after a loader failure. It
 * is strictly better than the environment-scoped operation log it replaces, but
 * a loader-only answer needs a destination probe the backoffice cannot make
 * today — `knowledge.ingestion_corpus_freshness` holds the per-environment
 * loader watermark and is service-role-only, and its records are incomplete
 * (datashake.fr reads `{test: 27}` while all three stores are populated).
 * Tracked in IX-4826.
 */
export async function fetchWebsiteEverIngested(workspaceId: string): Promise<boolean> {
    // Existence only — no `count: 'exact'`, which would scan the whole corpus to
    // produce a number nobody reads.
    const { data, error } = await supabase
        .schema('knowledge')
        .from('corpus_documents')
        .select('document_id')
        .eq('workspace_id', workspaceId)
        .eq('decision', 'include')
        .not('last_processed_at', 'is', null)
        .limit(1);

    if (error) {
        logger.error('Error checking website ingestion state:', error);
        throw new WebsiteSyncError('Failed to check website ingestion state');
    }

    return (data ?? []).length > 0;
}

export async function fetchWebsiteSyncRuns(workspaceId: string): Promise<WebsiteSyncRun[]> {
    const { data, error } = await supabase
        .schema('knowledge')
        .from('crawl_runs')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false })
        .limit(20);

    if (error) {
        logger.error('Error fetching website corpus runs:', error);
        throw new WebsiteSyncError('Failed to fetch website corpus runs');
    }

    return (data ?? []).map(mapRun);
}

export async function fetchWebsiteSyncPages(
    workspaceId: string,
    filters: WebsiteSyncPagesFilters = {}
): Promise<WebsiteSyncPagesResult> {
    const page = filters.page ?? 0;
    const pageSize = filters.pageSize ?? 100;
    let query = supabase
        .schema('knowledge')
        .from('corpus_documents')
        .select('*', { count: 'exact' })
        .eq('workspace_id', workspaceId)
        .eq('origin', 'scrape');

    if (filters.decision && filters.decision !== 'all') {
        query = query.eq('decision', filters.decision);
    }
    const search = filters.search?.trim();
    if (search) {
        const escaped = escapeIlikeSearch(search);
        query = query.or(
            `canonical_url.ilike.%${escaped}%,source_url.ilike.%${escaped}%,path.ilike.%${escaped}%,title.ilike.%${escaped}%,description.ilike.%${escaped}%,source_host.ilike.%${escaped}%`
        );
    }

    const { data, error, count } = await query
        .order('source_host', { ascending: true })
        .order('path', { ascending: true })
        .order('document_id', { ascending: true })
        .range(page * pageSize, page * pageSize + pageSize - 1);

    if (error) {
        logger.error('Error fetching website corpus pages:', error);
        throw new WebsiteSyncError('Failed to fetch website corpus pages');
    }

    return {
        items: (data ?? []).map(mapPage),
        total: count ?? 0,
    };
}

export async function fetchAllWebsiteSyncPages(
    workspaceId: string,
    filters: WebsiteSyncPagesFilters = {}
): Promise<WebsiteSyncPagesResult> {
    const allRows: CorpusDocumentRow[] = [];
    let total = 0;
    let from = 0;

    while (true) {
        let query = supabase
            .schema('knowledge')
            .from('corpus_documents')
            .select('*', { count: 'exact' })
            .eq('workspace_id', workspaceId)
            .eq('origin', 'scrape');

        if (filters.decision && filters.decision !== 'all') {
            query = query.eq('decision', filters.decision);
        }
        const search = filters.search?.trim();
        if (search) {
            const escaped = escapeIlikeSearch(search);
            query = query.or(
                `canonical_url.ilike.%${escaped}%,source_url.ilike.%${escaped}%,path.ilike.%${escaped}%,title.ilike.%${escaped}%,description.ilike.%${escaped}%,source_host.ilike.%${escaped}%`
            );
        }

        const { data, error, count } = await query
            .order('source_host', { ascending: true })
            .order('path', { ascending: true })
            .order('document_id', { ascending: true })
            .range(from, from + SUPABASE_PAGE_SIZE - 1);

        if (error) {
            logger.error('Error fetching all website corpus pages:', error);
            throw new WebsiteSyncError('Failed to fetch website corpus pages');
        }

        const rows = data ?? [];
        allRows.push(...rows);
        if (from === 0) total = count ?? rows.length;
        if (rows.length < SUPABASE_PAGE_SIZE) break;
        from += SUPABASE_PAGE_SIZE;
    }

    return {
        items: allRows.map(mapPage),
        total,
    };
}

const NEWLY_SELECTED_LIMIT = 1000;

/**
 * Count (and collect up to {@link NEWLY_SELECTED_LIMIT}) the currently-included
 * documents first discovered after `sinceIso`. When `sinceIso` is null the
 * corpus has never been ingested, so every included document qualifies.
 */
export async function fetchNewlySelectedIncludedDocuments(
    workspaceId: string,
    sinceIso: string | null
): Promise<NewlySelectedDocuments> {
    // No `count: 'exact'` — that forces a full filtered scan+sort of the corpus
    // just to size the banner. We only need up to LIMIT+1 ids to decide "any?"
    // and "more than one batch?"; the exact count above LIMIT is shown as "1000+".
    let query = supabase
        .schema('knowledge')
        .from('corpus_documents')
        .select('document_id')
        .eq('workspace_id', workspaceId)
        .eq('origin', 'scrape')
        .eq('decision', 'include');
    if (sinceIso) {
        query = query.gt('created_at', sinceIso);
    }

    const { data, error } = await query
        .order('created_at', { ascending: true })
        .limit(NEWLY_SELECTED_LIMIT + 1);

    if (error) {
        logger.error('Error detecting newly selected website pages:', error);
        throw new WebsiteSyncError('Failed to detect newly selected website pages');
    }

    const ids = (data ?? [])
        .map((row) => row.document_id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0);
    const truncated = ids.length > NEWLY_SELECTED_LIMIT;

    return {
        documentIds: ids.slice(0, NEWLY_SELECTED_LIMIT),
        total: truncated ? NEWLY_SELECTED_LIMIT : ids.length,
        truncated,
    };
}

async function adminFetch<T>(path: string, init: RequestInit): Promise<T> {
    const baseUrl = getAdminApiBaseUrl(fail);
    const requestUrl = buildApiUrl(baseUrl, path);
    const accessToken = await getSupabaseAccessToken(fail);

    let response: Response;
    try {
        response = await fetch(requestUrl.toString(), {
            ...init,
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
                ...init.headers,
            },
        });
    } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown network error';
        throw new WebsiteSyncError(`Network error: ${message}`);
    }

    if (!response.ok) {
        const detail = await parseErrorDetail(response);
        const suffix = detail ? `: ${detail}` : '';
        throw new WebsiteSyncError(
            `Website corpus request failed (${response.status})${suffix}`,
            response.status
        );
    }

    try {
        return (await response.json()) as T;
    } catch {
        throw new WebsiteSyncError('Failed to parse website corpus response');
    }
}

async function idempotentAdminFetch<T>(scope: string, path: string, init: RequestInit): Promise<T> {
    const requestKey = pendingRequestKey(scope);
    try {
        const result = await adminFetch<T>(path, {
            ...init,
            headers: { ...init.headers, 'Idempotency-Key': requestKey },
        });
        clearPendingRequestKey(scope);
        return result;
    } catch (error) {
        if (!shouldRetainRequestKey(error)) clearPendingRequestKey(scope);
        throw error;
    }
}

export async function fetchWebsiteSyncCapabilities(): Promise<WebsiteSyncCapabilities> {
    return adminFetch<WebsiteSyncCapabilities>('/admin/website-scraping/capabilities', {
        method: 'GET',
    });
}

export async function fetchUnfinishedWebsiteDocuments(
    workspaceId: string
): Promise<UnfinishedWebsiteDocuments> {
    return adminFetch<UnfinishedWebsiteDocuments>(
        `/admin/website-scraping/documents/unfinished?${new URLSearchParams({ workspace_id: workspaceId })}`,
        { method: 'GET' }
    );
}

export async function refreshWebsiteDocuments(
    args: RefreshWebsiteDocumentsArgs
): Promise<RefreshWebsiteDocumentsResponse> {
    const documentIds = [...new Set(args.documentIds)].sort();
    const scope = JSON.stringify([
        'website-selected-refresh',
        args.workspaceId.toLowerCase(),
        documentIds,
    ]);
    return idempotentAdminFetch<RefreshWebsiteDocumentsResponse>(
        scope,
        '/admin/website-scraping/documents/refresh',
        {
            method: 'POST',
            body: JSON.stringify({
                workspace_id: args.workspaceId,
                document_ids: documentIds,
                force_fetch: true,
            }),
        }
    );
}

export interface WebsiteSyncPageMarkdown {
    document_id: string;
    markdown: string;
}

/** Processed markdown ("Rose version") of one synced website page. */
export async function fetchWebsiteSyncPageMarkdown(
    documentId: string,
    workspaceId: string
): Promise<WebsiteSyncPageMarkdown> {
    return adminFetch<WebsiteSyncPageMarkdown>(
        `/admin/website-scraping/documents/${documentId}/body?workspace_id=${encodeURIComponent(workspaceId)}`,
        { method: 'GET' }
    );
}

/** The crawl roots this client syncs. */
export async function fetchWebsiteSources(workspaceId: string): Promise<WebsiteSource[]> {
    const query = new URLSearchParams({ workspace_id: workspaceId });
    return adminFetch<WebsiteSource[]>(`/admin/website-scraping/sources?${query.toString()}`, {
        method: 'GET',
    });
}

/** Staff-only: add a crawl root to this client. */
export async function addWebsiteSource(args: {
    workspaceId: string;
    rootUrl: string;
}): Promise<WebsiteSource> {
    return adminFetch<WebsiteSource>('/admin/website-scraping/sources', {
        method: 'POST',
        body: JSON.stringify({ workspace_id: args.workspaceId, root_url: args.rootUrl }),
    });
}

/** Staff-only: stop syncing a crawl root. Its pages stay in the corpus. */
export async function retireWebsiteSource(args: {
    workspaceId: string;
    websiteSourceId: string;
}): Promise<WebsiteSource> {
    const query = new URLSearchParams({ workspace_id: args.workspaceId });
    return adminFetch<WebsiteSource>(
        `/admin/website-scraping/sources/${encodeURIComponent(args.websiteSourceId)}/retire?${query.toString()}`,
        { method: 'POST' }
    );
}

/** Discover one website root and build a review. No pages are fetched or ingested. */
export async function startWebsiteRescan(
    args: StartWebsiteRescanArgs
): Promise<StartWebsiteRescanResponse> {
    const scope = `website-rescan:${args.workspaceId.toLowerCase()}:${args.websiteSourceId ?? 'only'}`;
    return idempotentAdminFetch<StartWebsiteRescanResponse>(
        scope,
        '/admin/website-scraping/rescan-reviews',
        {
            method: 'POST',
            body: JSON.stringify({
                workspace_id: args.workspaceId,
                website_source_id: args.websiteSourceId ?? null,
            }),
        }
    );
}

/** Restore the actionable website re-scan review for a workspaceId, if one exists. */
export async function fetchCurrentWebsiteRescanReview(
    workspaceId: string
): Promise<WebsiteRescanReviewSummary | null> {
    const query = new URLSearchParams({ workspace_id: workspaceId });
    const response = await adminFetch<CurrentWebsiteRescanReviewResponse>(
        `/admin/website-scraping/rescan-reviews/current?${query.toString()}`,
        { method: 'GET' }
    );
    return response.review;
}

const REVIEW_PAGE_SIZE = 200;
const REVIEW_CHANGE_KINDS: WebsiteRescanReviewChangeKind[] = ['added', 'missing', 'returning'];

async function fetchWebsiteRescanReviewPagesForKind(
    reviewId: string,
    workspaceId: string,
    changeKind: WebsiteRescanReviewChangeKind
): Promise<WebsiteRescanReviewPage[]> {
    const items: WebsiteRescanReviewPage[] = [];
    let cursor: string | null = null;

    do {
        const query = new URLSearchParams({
            change_kind: changeKind,
            limit: String(REVIEW_PAGE_SIZE),
            workspace_id: workspaceId,
        });
        if (cursor) query.set('cursor', cursor);
        const response = await adminFetch<WebsiteRescanReviewPagesResponse>(
            `/admin/website-scraping/rescan-reviews/${encodeURIComponent(reviewId)}/pages?${query.toString()}`,
            { method: 'GET' }
        );
        items.push(...response.items);
        cursor = response.next_cursor;
    } while (cursor);

    return items;
}

/** Load every changed review row without silently truncating a large site. */
export async function fetchAllWebsiteRescanReviewPages(
    reviewId: string,
    workspaceId: string
): Promise<WebsiteRescanReviewPagesResult> {
    const groups = await Promise.all(
        REVIEW_CHANGE_KINDS.map((kind) =>
            fetchWebsiteRescanReviewPagesForKind(reviewId, workspaceId, kind)
        )
    );
    return { items: groups.flat() };
}

/** Apply one frozen review. Missing pages are removed unless explicitly kept. */
export async function applyWebsiteRescanReview(
    args: ApplyWebsiteRescanReviewArgs
): Promise<ApplyWebsiteRescanReviewResponse> {
    const scope = JSON.stringify([
        'website-rescan-review-apply',
        args.workspaceId.toLowerCase(),
        args.reviewId,
        [...args.selectedPageIds].sort(),
        [...args.keptMissingPageIds].sort(),
        args.refreshExistingPages,
    ]);
    return idempotentAdminFetch<ApplyWebsiteRescanReviewResponse>(
        scope,
        `/admin/website-scraping/rescan-reviews/${encodeURIComponent(args.reviewId)}/apply?${new URLSearchParams({ workspace_id: args.workspaceId }).toString()}`,
        {
            method: 'POST',
            body: JSON.stringify({
                selected_page_ids: args.selectedPageIds,
                kept_missing_page_ids: args.keptMissingPageIds,
                refresh_existing_pages: args.refreshExistingPages,
            }),
        }
    );
}

/** Resolve a pending review without changing the website catalogue or knowledge base. */
export async function dismissWebsiteRescanReview(
    args: DismissWebsiteRescanReviewArgs
): Promise<CurrentWebsiteRescanReviewResponse> {
    return idempotentAdminFetch<CurrentWebsiteRescanReviewResponse>(
        `website-rescan-review-dismiss:${args.workspaceId.toLowerCase()}:${args.reviewId}`,
        `/admin/website-scraping/rescan-reviews/${encodeURIComponent(args.reviewId)}/dismiss?${new URLSearchParams({ workspace_id: args.workspaceId }).toString()}`,
        {
            method: 'POST',
            body: JSON.stringify({}),
        }
    );
}

export async function updateWebsitePageDecision(
    args: UpdatePageDecisionArgs
): Promise<DecisionUpdateResponse> {
    const scope = JSON.stringify([
        'website-policy-document',
        args.pageId,
        args.decision,
        args.reason ?? null,
        args.persistOverride ?? true,
    ]);
    return idempotentAdminFetch<DecisionUpdateResponse>(
        scope,
        `/admin/website-scraping/documents/${args.pageId}/decision`,
        {
            method: 'PATCH',
            body: JSON.stringify({
                workspace_id: args.workspaceId,
                decision: args.decision,
                reason: args.reason ?? null,
                persist_override: args.persistOverride ?? true,
            }),
        }
    );
}

export async function bulkUpdateWebsitePageDecision(
    args: BulkUpdatePageDecisionArgs
): Promise<DecisionUpdateResponse> {
    const scope = JSON.stringify([
        'website-policy-run',
        args.runId,
        [...args.pageIds].sort(),
        args.decision,
        args.reason ?? null,
        args.persistOverride ?? true,
    ]);
    return idempotentAdminFetch<DecisionUpdateResponse>(
        scope,
        `/admin/website-scraping/runs/${args.runId}/pages/decision`,
        {
            method: 'PATCH',
            body: JSON.stringify({
                page_ids: args.pageIds,
                workspace_id: args.workspaceId,
                decision: args.decision,
                reason: args.reason ?? null,
                persist_override: args.persistOverride ?? true,
            }),
        }
    );
}

export async function bulkUpdateWebsiteDocumentDecision(
    args: BulkUpdateDocumentDecisionArgs
): Promise<DecisionUpdateResponse> {
    const scope = JSON.stringify([
        'website-policy-workspaceId',
        args.workspaceId.toLowerCase(),
        [...args.documentIds].sort(),
        args.decision,
        args.reason ?? null,
        args.persistOverride ?? true,
    ]);
    return idempotentAdminFetch<DecisionUpdateResponse>(
        scope,
        '/admin/website-scraping/documents/decision',
        {
            method: 'PATCH',
            body: JSON.stringify({
                workspace_id: args.workspaceId,
                document_ids: args.documentIds,
                decision: args.decision,
                reason: args.reason ?? null,
                persist_override: args.persistOverride ?? true,
            }),
        }
    );
}

export async function applyWebsiteDocumentPolicyChanges(
    args: ApplyWebsiteDocumentPolicyChangesArgs
): Promise<DecisionUpdateResponse> {
    const scope = JSON.stringify([
        'website-policy-changes',
        args.workspaceId.toLowerCase(),
        [...args.includeDocumentIds].sort(),
        [...args.excludeDocumentIds].sort(),
        args.reason ?? null,
    ]);
    return idempotentAdminFetch<DecisionUpdateResponse>(
        scope,
        '/admin/website-scraping/documents/policy-changes',
        {
            method: 'POST',
            body: JSON.stringify({
                workspace_id: args.workspaceId,
                include_document_ids: args.includeDocumentIds,
                exclude_document_ids: args.excludeDocumentIds,
                reason: args.reason ?? null,
            }),
        }
    );
}
